/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isEmpty } from 'lodash'

import { rootUrl } from '@modules/utils/rootUrls'
import { loginUrl } from '@modules/auth/urls'
import { getQuerystringItems } from '@modules/utils/qs'
import { updateAuthExpirationFromToken } from '@modules/auth/auth'

import type { Location } from 'history'
import type { RouteComponentProps } from 'react-router'

interface QueryStringParameters {}

interface StateProps {}

type DispatchProps = {
  saveAuthExpiration: (sessionExpirationTime: string) => void
  redirect: (redirectTo: string) => void
}

type ConsumerProps = RouteComponentProps<QueryStringParameters>

export type Props = StateProps & DispatchProps & ConsumerProps

function getToken(token: string): string {
  return token.replace(`#`, ``)
}

export function getTokenAndStateThenRedirect({
  saveAuthExpiration,
  redirect,
  location,
}: {
  saveAuthExpiration: (sessionExpirationTime: string) => void
  redirect: (redirectTo: string) => void
  location: Props['location']
}) {
  const { hash } = location

  // if for whatever reason we don't have a hash, then we don't have a token or a state, so just fail gracefully
  if (isEmpty(hash)) {
    return redirect(loginUrl())
  }

  // The API sends back the nextState and the token in this format:
  // /sso/token/#:tokenId&state=%7B%22redirectTo%22:%22/login%22%7D
  // So first we split off the tokenId and the state and parse each one to get us the values we need
  const splitHash = hash.split(`&`)

  // Guaranteed to get at least one element back from the split
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = getToken(splitHash[0]!)

  // the token can sometimes be empty, in which case they can't login anyways so fail gracefully
  if (isEmpty(token)) {
    return redirect(loginUrl())
  }

  const expirationTime = getSessionExpirationTime(location)

  if (expirationTime) {
    saveAuthExpiration(expirationTime)
  } else {
    // when hash contains no expiration
    updateAuthExpirationFromToken(token)
  }

  redirect(getTokenRedirect(location))
}

/**
 * @param {Location} location The URL that contains the redirectTo in the search query
 * @return {string} redirectTo: e.g /deployments
 */
function getTokenRedirect({ search }: Location): string {
  const queryParams = getQuerystringItems(search)

  // if there is no `state` in the url, in which case it is undefined
  if (!queryParams.state) {
    return rootUrl()
  }

  return JSON.parse(queryParams.state).redirectTo
}

/**
 * @param {Location} location The URL that contains the session_expiration_time in the hash
 * @return {string | null | undefined} The session expiration time
 */
export const getSessionExpirationTime = ({ hash }: Location): string | null | undefined =>
  getQuerystringItems(hash).session_expiration_time
